// Coordinates juice in response to sim events: impacts → hitstop+shake+particles+squash;
// round end → slow-mo + dolly + dissolve.

import * as THREE from 'three';
import { CONFIG } from '../config';
import { Bone, BONE_COUNT } from '../sim/Ragdoll';
import type { ImpactEvent, SimState } from '../sim/SimState';
import type { CharacterView } from '../render/CharacterView';
import type { Renderer } from '../render/Renderer';
import { AmbientLoop, AudioBus, Music, PositionalLoop, PositionalOneShot } from '../render/GameAudio';
import { HitStop } from './HitStop';
import { KoReplay } from './KoReplay';
import { ScreenShake } from './ScreenShake';
import { SlowMo } from './SlowMo';

// Lowpass cutoff endpoints for the KO replay. Bottom (at playbackRate=0.25)
// is the muffled-underwater reading the user asked for; top is effectively
// bypass. Interpolated in log-frequency space so the ear hears an even sweep
// rather than a change that's all back-loaded in the top octave.
const REPLAY_CUTOFF_MIN_HZ = 300;
const REPLAY_CUTOFF_MAX_HZ = AudioBus.BYPASS_HZ;
const REPLAY_RATE_MIN = 0.25;
const REPLAY_RATE_MAX = 1.0;
function cutoffForRate(rate: number): number {
  const r = Math.max(REPLAY_RATE_MIN, Math.min(REPLAY_RATE_MAX, rate));
  const t = (r - REPLAY_RATE_MIN) / (REPLAY_RATE_MAX - REPLAY_RATE_MIN);
  const lo = Math.log(REPLAY_CUTOFF_MIN_HZ);
  const hi = Math.log(REPLAY_CUTOFF_MAX_HZ);
  return Math.exp(lo + (hi - lo) * t);
}

// Per-sample dry gain (0..1). Footsteps were pre-tuned at 0.2; hits, swings,
// and thruster match the user-requested ~0.3.
const FOOTSTEP_VOLUME = 2.7;
const HIT_VOLUME = 2;
const SWING_VOLUME = 2.5;
const THRUSTER_VOLUME = 1;
const SPLASH_VOLUME = 1.0;

// Per-sample reverb send (0..1). Independent of dry volume above — tuning
// these changes how "wet" each SFX sounds in the shared convolution tail
// without touching its loudness in the direct mix. Only maps whose
// `audio.reverb` is set in map.json actually wire reverb; on dry maps
// these values are ignored.
const DEFAULT_FOOTSTEP_REVERB = 0.15;
const DEFAULT_HIT_REVERB = 0.07;
const DEFAULT_SWING_REVERB = 10;
const THRUSTER_REVERB = 0.05;
const DEFAULT_SPLASH_REVERB = 0.2;

const HIT_URL = 'sounds/hit.ogg';
const SWING_URL = 'sounds/swing.ogg';
const THRUSTER_URL = 'sounds/thruster.ogg';

export class FxBus {
  readonly hitStop = new HitStop();
  readonly shake: ScreenShake;
  readonly slowMo = new SlowMo();
  readonly koReplay: KoReplay;
  // Positional audio sources. Hit + swing play on every map; footsteps +
  // thruster are currently spaceship-only (no samples wired for the others
  // yet for footsteps; thrusters only exist on the spaceship).
  // Public so external sources (e.g. the tutorial NarratorEngine) can
  // route their own custom audio chains through the same SFX bus.
  readonly audioBus: AudioBus;
  private readonly hitAudio: PositionalOneShot;
  private readonly swingAudio: PositionalOneShot;
  private readonly footstepAudio: PositionalOneShot | null = null;
  /** Optional alternate footstep pool used when a foot plants below the
   *  map's `audio.footsteps.waterY` threshold. Null when the map doesn't
   *  declare a `waterUrls` set. */
  private readonly waterFootstepAudio: PositionalOneShot | null = null;
  private readonly waterFootstepY: number = -Infinity;
  /** Splash one-shot pool for the swimming mechanic. Round-robin across the
   *  `audio.swimSplash.urls` array via the same `nextBuffer` counter used
   *  by footsteps. Null when the map declares no splash samples. */
  private readonly swimSplashAudio: PositionalOneShot | null = null;
  private readonly thrusterAudio: PositionalLoop | null = null;
  private readonly music: Music | null = null;
  /** Tracks whether startMusic() has already fired so back-to-back calls
   *  don't restart the buffer mid-track. */
  private musicStarted = false;
  /** Non-positional ambient beds (ocean, ship-creak, etc.) — declared by
   *  `audio.ambientLoops` in map.json. Held so the back-to-lobby fade can
   *  ramp them down alongside music + thruster. */
  private readonly ambientLoops: AmbientLoop[] = [];
  /** Optional progress-driven loop. Volume is rewritten every render frame
   *  from a behavior's `sinkProgress` accessor (sinking-ship map). */
  private readonly sinkLoop: AmbientLoop | null = null;
  private readonly sinkLoopBehaviorId: string | null = null;
  private readonly sinkLoopFullAtProgress: number = 0.5;
  private readonly sinkLoopPeak: number = 1.0;
  /** Optional looped bed that plays only while the follow-cam is below the
   *  map's `waterY`. Built when `audio.underwaterLoop.url` is present;
   *  start/stop on submerge edges. Null when the map declares none — the
   *  underwater post pass + lowpass still run independently of this. */
  private readonly underwaterLoop: AmbientLoop | null = null;
  private underwaterLoopPlaying = false;
  /** Smoothed 0..1 underwater intensity. Ramps toward 1 when the camera
   *  is below the map's water surface and toward 0 above; the rate comes
   *  from `CONFIG.render.underwater.intensityRampSec`. Drives the post
   *  pass uniform every frame so dives / surfaces don't pop. */
  private underwaterIntensity = 0;
  private dissolve = 0;
  private targetDissolve = 0;
  private prevRoundOver = false;
  private prevHp = new Map<number, number>();
  // Reused once per impact event so we don't allocate a Vector3 per hit.
  private _impactDir = new THREE.Vector3();
  /** Last sim tick whose discrete events (impacts, footsteps, swings) we
   *  processed. `fx.step` runs once per RENDER frame, but `latestSnapshot()`
   *  returns the same sim snapshot across multiple render frames whenever
   *  render > sim rate (typical: 120 Hz render, 60 Hz sim). Without this
   *  gate we'd replay every event 2× with a ~8 ms offset — audible as
   *  phasing/chorus on short SFX and re-firing hitstop/particles on impacts. */
  private lastProcessedTick = -1;

  constructor(private renderer: Renderer, localPlayerId = 0) {
    this.shake = new ScreenShake(renderer.cameraRig);
    this.koReplay = new KoReplay(localPlayerId);

    // Audio config comes from the map bundle's map.json. A dry map (no
    // `audio.reverb`, no `audio.footsteps`, no `audio.music`) produces an
    // FxBus that only plays the cross-map hit/swing samples without any
    // environmental reverb. The spaceship map declares all three.
    const audioDef = renderer.arena.audio;
    const reverbCfg = audioDef?.reverb ?? null;
    this.audioBus = new AudioBus(renderer.cameraRig.listener, renderer.scene, reverbCfg);

    // Cross-map SFX — every map loads hit + swing. The wet sends fall back
    // to the built-in defaults; a map can override via audio.hitReverbWet /
    // audio.swingReverbWet when present.
    this.hitAudio = new PositionalOneShot(this.audioBus, [HIT_URL], {
      poolSize: 6,
      dryGain: HIT_VOLUME,
      wetGain: audioDef?.hitReverbWet ?? DEFAULT_HIT_REVERB
    });
    this.swingAudio = new PositionalOneShot(this.audioBus, [SWING_URL], {
      poolSize: 6,
      dryGain: SWING_VOLUME,
      wetGain: audioDef?.swingReverbWet ?? DEFAULT_SWING_REVERB
    });

    // Footsteps — only maps that declare `audio.footsteps` get them. The
    // urls come from map.json so a future grass / wood / concrete map can
    // ship its own sample set without any code change here. A second
    // optional pool drives water / splash samples whenever a foot plants
    // below `waterY` — used by the sinking-ship map.
    const footsteps = audioDef?.footsteps;
    if (footsteps && footsteps.urls.length > 0) {
      this.footstepAudio = new PositionalOneShot(this.audioBus, footsteps.urls, {
        poolSize: 12,
        dryGain: footsteps.volume ?? FOOTSTEP_VOLUME,
        wetGain: footsteps.reverbWet ?? DEFAULT_FOOTSTEP_REVERB
      });
      if (footsteps.waterUrls && footsteps.waterUrls.length > 0) {
        this.waterFootstepAudio = new PositionalOneShot(this.audioBus, footsteps.waterUrls, {
          poolSize: 12,
          dryGain: footsteps.volume ?? FOOTSTEP_VOLUME,
          wetGain: footsteps.reverbWet ?? DEFAULT_FOOTSTEP_REVERB
        });
        this.waterFootstepY = footsteps.waterY ?? 0;
      }
    }

    // Splash SFX — same round-robin one-shot pattern as footsteps.
    // Triggered on swim entry (gated on impact velocity sim-side) and on
    // each debounced stroke while the player is paddling.
    const splash = audioDef?.swimSplash;
    if (splash && splash.urls.length > 0) {
      this.swimSplashAudio = new PositionalOneShot(this.audioBus, splash.urls, {
        poolSize: 8,
        dryGain: splash.volume ?? SPLASH_VOLUME,
        wetGain: splash.reverbWet ?? DEFAULT_SPLASH_REVERB
      });
    }

    // Ambient loops — looped non-positional beds. Built once at startup; we
    // hold off start() until `startMusic()` fires so they don't bleed under
    // the still-visible map-vote / map-select transition wipe (FxBus is
    // constructed during the preload phase). Fade-out happens on dispose /
    // lobby return.
    for (const def of audioDef?.ambientLoops ?? []) {
      const loop = new AmbientLoop(this.audioBus, def.url, def.volume ?? 0.5);
      this.ambientLoops.push(loop);
    }

    // Underwater bed — non-positional ambient loop, kept stopped until the
    // follow-cam dips below the map's water surface. Construction kicks
    // off the buffer decode immediately so the first dive doesn't audibly
    // hitch on first-time load.
    if (audioDef?.underwaterLoop?.url) {
      const uw = audioDef.underwaterLoop;
      this.underwaterLoop = new AmbientLoop(this.audioBus, uw.url, uw.volume ?? 0.5);
    }

    // Sink-progress loop — gain follows sinkProgress on the named behavior.
    // We start it at zero so it's effectively silent until the ship
    // actually begins descending.
    if (audioDef?.sinkLoop) {
      const sl = audioDef.sinkLoop;
      this.sinkLoop = new AmbientLoop(this.audioBus, sl.url, 0);
      this.sinkLoopBehaviorId = sl.behaviorId ?? 'sink';
      this.sinkLoopFullAtProgress = sl.fullAtProgress ?? 0.5;
      this.sinkLoopPeak = sl.volume ?? 1.0;
      // Deferred to startMusic() — see ambient-loop note above.
    }

    // Background music — the map declares its own URL if it has one. We
    // construct (which decodes the buffer) but DON'T call start() here:
    // FxBus is built during the map-select preload, before the transition
    // wipe finishes. Caller drives playback via startMusic() so the music
    // doesn't bleed under the still-visible vote / map-select screen.
    //
    // Per-source level is the map's natural music loudness (or 1.0 if
    // unspecified). The user's music slider scales this on the bus.
    if (audioDef?.music) {
      const vol = audioDef.musicVolume ?? 1.0;
      this.music = new Music(this.audioBus, audioDef.music, vol);
    }

    // Thruster loop — still coupled to shipGroup presence rather than a
    // separate audio field. When a map has a ship (registered under id
    // 'ship' by a behavior), we parent a looping thruster sample under it
    // so the sound banks with the hull. Any map without a shipGroup skips.
    const shipGroup = renderer.arena.shipGroup;
    if (shipGroup) {
      const a = CONFIG.render.ionThrusters.thrusterA;
      const b = CONFIG.render.ionThrusters.thrusterB;
      const thruster = new PositionalLoop(this.audioBus, THRUSTER_URL, {
        dryGain: THRUSTER_VOLUME, wetGain: THRUSTER_REVERB
      });
      thruster.attachTo(shipGroup, (a.posX + b.posX) * 0.5, (a.posY + b.posY) * 0.5, (a.posZ + b.posZ) * 0.5);
      // Deferred to startMusic() — see ambient-loop note above.
      this.thrusterAudio = thruster;
    }
  }

  step(realDt: number, snap: SimState): void {
    // Discrete-event processing is GATED to one-per-sim-tick. Continuous
    // state (damage flash, round transitions, dissolve smoothing, hitstop /
    // slowmo decay) still runs every render frame below — those need the
    // high-frequency updates.
    const newTick = snap.tick !== this.lastProcessedTick;
    if (newTick) {
      // Impacts → particles + flash + shake + hitstop + bone kick
      for (const im of snap.impacts) this.processImpact(im, snap);

      // Hit audio — fire on every impact. Punches, jump-kicks, and
      // headbutts share the same hit sample.
      for (const im of snap.impacts) {
        this.hitAudio.play(im.x, im.y, im.z);
      }

      // Swings → whoosh at press time, at the hand's world position.
      for (const sw of snap.swings) this.swingAudio.play(sw.x, sw.y, sw.z);

      // Footsteps → branched on foot Y vs the map's `waterY` threshold
      // when a water-sample pool is configured (sinking-ship). Otherwise
      // every plant uses the primary pool (wood / metal / etc).
      if (this.footstepAudio) {
        for (const fs of snap.footsteps) {
          const useWater = this.waterFootstepAudio && fs.y < this.waterFootstepY;
          const pool = useWater ? this.waterFootstepAudio! : this.footstepAudio;
          pool.play(fs.x, fs.y, fs.z);
        }
      }

      // Splashes → swim entry + per-stroke pulses, round-robin across the
      // map's swimSplash.urls pool.
      if (this.swimSplashAudio) {
        for (const sp of snap.splashes) this.swimSplashAudio.play(sp.x, sp.y, sp.z);
      }

      this.lastProcessedTick = snap.tick;
    }

    // Damage flash on view when HP drops noticeably since last frame.
    for (const ch of snap.characters) {
      const prev = this.prevHp.get(ch.id) ?? ch.hp;
      if (prev - ch.hp > 0.01) {
        const view = this.renderer.characterViews.get(ch.id);
        view?.pulseDamageFlash();
      }
      this.prevHp.set(ch.id, ch.hp);
    }

    // Round transitions
    if (snap.roundOver && !this.prevRoundOver) {
      this.slowMo.trigger();
      this.renderer.cameraRig.addDolly(1.2);
      this.targetDissolve = 1;
    }
    if (!snap.roundOver && this.prevRoundOver) {
      // Reset visuals
      this.targetDissolve = 0;
      this.shake.reset();
      // Kill any lingering time-dilation so the new round renders at 1×
      // immediately. Without these, a KoReplay still in 'slowmo'/'ramp' at
      // reset (it only self-exits from 'hold') would keep the render head
      // behind live, and a half-finished SlowMo ramp would leave the scene
      // still slightly slowed — both read as stuck / laggy input at spawn.
      this.slowMo.reset();
      this.koReplay.reset();
    }
    this.prevRoundOver = snap.roundOver;

    // Smooth dissolve
    const ds = this.targetDissolve > this.dissolve ? 0.6 : 0.9;
    this.dissolve += (this.targetDissolve - this.dissolve) * Math.min(1, realDt * 4 * ds);
    this.renderer.setArenaDissolve(this.dissolve);

    this.hitStop.step(realDt);
    this.slowMo.step(realDt);

    // The user-facing music volume now lives on the AudioBus's musicGain,
    // not on the source. The per-source level here is the map's natural
    // loudness (set at construction); the bus scales it. So no per-frame
    // rewrite is needed — and shouldn't happen, since it would stomp the
    // map-tuned source level back to whatever's in CONFIG.audio.
    void this.suspendMusicVolumeSync;

    // Sink-loop crescendo — gain ramps with the ship's sink progress.
    // `fullAtProgress` lets the map say "full volume by half submersion"
    // without changing the behavior's curve; we just clamp + scale.
    if (this.sinkLoop && this.sinkLoopBehaviorId && !this.suspendMusicVolumeSync) {
      const beh = this.renderer.arena.getBehavior(this.sinkLoopBehaviorId) as
        | { sinkProgress?: number }
        | undefined;
      const p = beh?.sinkProgress ?? 0;
      const denom = Math.max(1e-3, this.sinkLoopFullAtProgress);
      const v = Math.max(0, Math.min(1, p / denom)) * this.sinkLoopPeak;
      this.sinkLoop.setVolume(v);
    }

    // Underwater state. Driven by the follow-cam's world Y vs the map's
    // `waterY` — drives both the post pass (wavey on-screen split) and
    // the audio lowpass / optional ambient loop. Done here in step() so
    // every render frame ramps continuously, even when the sim tick
    // hasn't advanced.
    const camY = this.renderer.cameraRig.camera.position.y;
    const waterY = this.renderer.arena.waterY;
    const uw = CONFIG.render.underwater;
    if (waterY !== undefined) {
      // Visual: the post pass is ALWAYS on while the map has water — the
      // per-pixel mask (worldY <= waterY + maskClearance, depth-clamped)
      // does all the work on its own, regardless of camera state. Any
      // pixel whose visible scene geometry sits below the surface line
      // picks up the tint / fog / refraction; anything above passes
      // through. So the same wave-edge clip operates from above water
      // (legs dipped in get fog) and from below water (head poking out
      // doesn't), without the whole-screen pop the previous gate was
      // producing.
      this.underwaterIntensity = 1;
      this.renderer.post?.setUnderwater(1, waterY);

      // Audio side keeps its camera-in-water trigger — that's the only
      // thing that should snap on / off based on submersion. surfaceBias
      // lifts the perceived surface a touch above the water so the
      // lowpass / loop engage just before the camera dips into the
      // physical water mesh. Wave-aware: on maps with a wave field the
      // surface is sampled at the camera's (x, z), so a crest passing
      // over the camera engages the lowpass even though the flat plane
      // is below — matching the visible post-pass wave silhouette.
      const camPos = this.renderer.cameraRig.camera.position;
      const surfaceAtCam =
        this.renderer.arena.waterSurfaceYAt(camPos.x, camPos.z) ?? waterY;
      const perceivedSurfaceY = surfaceAtCam + uw.surfaceBias;
      const halfUnder = camY < perceivedSurfaceY;
      const replayCutoff = cutoffForRate(this.koReplay.playbackRate());
      const underwaterCutoff = halfUnder ? uw.cutoffHz : AudioBus.BYPASS_HZ;
      this.audioBus.setFilterCutoff(Math.min(replayCutoff, underwaterCutoff));

      // Underwater bed loop. Edge-triggered — start when crossing under,
      // stop when surfacing.
      if (this.underwaterLoop) {
        if (halfUnder && !this.underwaterLoopPlaying) {
          this.underwaterLoop.start();
          this.underwaterLoopPlaying = true;
        } else if (!halfUnder && this.underwaterLoopPlaying) {
          this.underwaterLoop.stop();
          this.underwaterLoopPlaying = false;
        }
      }
    } else {
      // Dry map — make sure the post pass and audio paths sit at
      // identity so a previous water map's state can't leak through.
      if (this.underwaterIntensity !== 0) {
        this.underwaterIntensity = 0;
        // waterY very low so even a stray reconstruction can't trigger
        // the mask (intensity=0 already gates the multiply, but this
        // belt-and-braces avoids a pop on the very first frame after a
        // dry-map switch).
        this.renderer.post?.setUnderwater(0, -10000);
      }
      this.audioBus.setFilterCutoff(cutoffForRate(this.koReplay.playbackRate()));
      if (this.underwaterLoop && this.underwaterLoopPlaying) {
        this.underwaterLoop.stop();
        this.underwaterLoopPlaying = false;
      }
    }
  }

  /** Final timeScale applied to render dt. Includes the KO replay rate so
   *  within-frame view animations (CharacterView easing, particle update)
   *  slow alongside the replay head during slowmo and speed up on catchup. */
  timeScale(): number {
    return this.hitStop.factor() * this.slowMo.factor() * this.koReplay.playbackRate();
  }

  reset(): void {
    this.hitStop.reset();
    this.slowMo.reset();
    this.koReplay.reset();
    this.shake.reset();
    this.targetDissolve = 0;
    this.dissolve = 0;
    this.prevRoundOver = false;
    this.prevHp.clear();
    this.lastProcessedTick = -1;
    this.audioBus.setFilterCutoff(AudioBus.BYPASS_HZ);
    if (this.underwaterLoop && this.underwaterLoopPlaying) {
      this.underwaterLoop.stop();
      this.underwaterLoopPlaying = false;
    }
    this.underwaterIntensity = 0;
    this.renderer.post?.setUnderwater(0, -10000);
  }

  /** Begin playing the map's background music. Constructor decodes the
   *  buffer but defers playback so the music doesn't bleed under the
   *  in-flight transition wipe. Caller invokes this once the wipe is
   *  fully off-screen. Idempotent: calling twice on the same FxBus is a
   *  no-op (THREE.Audio.play() on an already-playing source is harmless,
   *  but skip the call so we don't restart the buffer). */
  startMusic(): void {
    if (this.musicStarted) return;
    this.musicStarted = true;
    this.music?.start();
    // Ambient beds, sink-progress loop, and thruster are constructed in the
    // ctor (so buffers decode early) but their start() is deferred to here
    // so map SFX doesn't bleed under the in-flight transition wipe.
    for (const a of this.ambientLoops) a.start();
    this.sinkLoop?.start();
    this.thrusterAudio?.start();
  }

  /** Fade the level music + thruster loop to silence over `durationMs`,
   *  then stop them. `step()` keeps rewriting the music volume from
   *  CONFIG.audio.musicVolume every frame, so we suspend that rewrite for
   *  the duration of the fade by pointing the music via a private flag.
   *  Resolves when the fade completes. One-shot SFX already in flight
   *  (hit/swing/footstep) play out naturally — they're short enough that
   *  their tail is inaudible by the time the UI transitions. */
  fadeAndStopAudio(durationMs: number): Promise<void> {
    this.suspendMusicVolumeSync = true;
    // Music source's per-track natural level (the music bus scales this on
    // top via the user slider). Read from THREE.Audio's gain so the fade
    // starts from whatever level the source is currently at, regardless of
    // any per-track override the map specified.
    const startMusic = this.music ? this.music.getVolume() : 0;
    const thruster = this.thrusterAudio;
    // Capture each ambient loop's current gain so the fade is relative to
    // whatever level it was riding at — avoids snapping a sub-peak loop
    // (e.g. sink-loop mid-crescendo) up to 1 just to fade it back down.
    const ambient = this.ambientLoops.slice();
    const sink = this.sinkLoop;
    // THREE.Audio has no public gain ramp — we read getVolume()/setVolume()
    // each frame. Use a rAF-driven ramp so the audio thread smooths itself.
    const t0 = performance.now();
    return new Promise<void>((resolve) => {
      const step = (): void => {
        const t = Math.min(1, (performance.now() - t0) / durationMs);
        const k = 1 - t;
        if (this.music) this.music.setVolume(startMusic * k);
        if (thruster) thruster.setVolume(k);
        for (const a of ambient) a.setVolume(0.5 * k);
        if (sink) sink.setVolume(this.sinkLoopPeak * k);
        if (t >= 1) {
          this.music?.stop();
          thruster?.stop();
          for (const a of ambient) a.stop();
          sink?.stop();
          resolve();
          return;
        }
        requestAnimationFrame(step);
      };
      requestAnimationFrame(step);
    });
  }

  /** Teardown hook — called by main's back-to-lobby path. Stops any
   *  still-playing continuous audio and disconnects the audio graph so the
   *  next match constructs a clean bus. Also drops the AudioBus from the
   *  live registry so subsequent volume-slider drags don't fan out to a
   *  dead bus. */
  dispose(): void {
    this.music?.stop();
    this.thrusterAudio?.stop();
    for (const a of this.ambientLoops) a.stop();
    this.sinkLoop?.stop();
    this.audioBus.dispose();
  }

  private suspendMusicVolumeSync = false;

  private processImpact(im: ImpactEvent, snap: SimState): void {
    this.shake.punch(im.strength);
    this.hitStop.trigger(im.strength);

    // Bone kick on the victim's torso & hit area. Walk `snap.characters`
    // once to find the victim instead of two separate `Array.find()` calls,
    // and reuse a member scratch Vector3 for the normalised kick direction.
    const view = this.renderer.characterViews.get(im.victimId);
    if (view) {
      let vx = 0, vz = 0;
      for (let i = 0; i < snap.characters.length; i++) {
        const c = snap.characters[i]!;
        if (c.id === im.victimId) { vx = c.pelvisX; vz = c.pelvisZ; break; }
      }
      const dir = this._impactDir.set(im.x - vx, 0.4, im.z - vz).normalize();
      view.kickBone(Bone.Torso, dir, Math.min(0.45, im.strength * 0.012));
      // Random other bone for chaos
      const rb = (Math.random() * BONE_COUNT) | 0;
      view.kickBone(rb, dir, Math.min(0.35, im.strength * 0.009));
    }
  }
}
